import { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Image,
  Form,
} from "antd";
import dayjs from 'dayjs';
import { getConversationList, getCTAImg } from "../services/ctaService";
import { useNavigate } from "react-router-dom";
import StatusModal from './StatusModal';
import { updateCTAStatus } from "../services/ctaService.js";
import errorImg from '../assets/screenshotError.png';
import { useGlobalState } from '../hooks/useGlobalState.js'
const { Title } = Typography;

const CTADetail = ({ data, pageState }) => {
  const [conversationList, setConversationList] = useState([]);
  const [imgUrl, setImgUrl] = useState('');
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalForm] = Form.useForm();
  const { userProfile } = useGlobalState();
  const conversationColumns = [
    {
      title: "Conversation ID",
      dataIndex: "conversationId",
      key: "conversationId",
      width: "20%",
    },
    {
      title: "Report number",
      dataIndex: "reportName",
      key: "reportName",
      width: "20%",
    },
    {
      title: "CTA origin content",
      dataIndex: "cta",
      key: "cta",
      width: "35%",
      render: (text) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text}
        </div>
      ),
    },
    {
      title: "Conversation time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "15%",
      render: (time) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <a
          href={`/conversations/${record.externalRef}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/conversations/${record.externalRef}`, {
              state: {
                conversation: record,
                from: {
                  pathname: '/cta',
                  pageInfo: pageState?.pageInfo,
                  filters: pageState?.filters,
                  sorter: pageState?.sorter,
                  ctaData: data
                }
              },
            });
          }}
        >
          <Button type="link" style={{ padding: 0 }}>
            View
          </Button>
        </a>
      ),
    },
  ];

  useEffect(() => {
    const companyId = userProfile?.user?.companyId ?? null;
    getConversationList(data.externalRef, companyId).then((res) => {
      setConversationList(res);
    });
    if (data.img == "1") {
      getCTAImg(data.externalRef).then((res) => {
        setImgUrl(res);
      });
    }

  }, [data]);

  const renderStatus = (status) => {
    const colorMap = {
      "Under Review": "processing",
      Verified: "success",
      Submitted: "warning",
      "Disruption In Progress": "gold",
      "Verified Genuine": "default",
      "Verified Scam": "red",
      "Notify Brand": "cyan",
      "Scam Disrupted": "purple",
    };
    return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
  };

  const handleModalOk = async () => {
    try {
      const values = await modalForm.validateFields();
      await updateCTAStatus({
        ctas: [data.externalRef],
        status: values.status,
      });

      setIsModalVisible(false);
      modalForm.resetFields();

      // 刷新数据
      data.ctaStatus = values.status;
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Row gutter={[0, 16]}>
      <Col span={12} key="left-column">
        <Card title={<Title level={5}>CTA information</Title>}>
          <Card key="status-card">
            <Row align="middle" gutter={8}>
              <Col key="status-label" span={14}>Status:</Col>
              <Col key="status-tag" span={5}>{renderStatus(data.ctaStatus)}</Col>
              {userProfile?.user?.userStatus !== "Company User" && (
                <Col key="status-button" span={5}>
                  <Button
                    type="link"
                    onClick={() => {
                      setIsModalVisible(true)
                      modalForm.setFieldsValue({ status: data.ctaStatus });
                    }}
                  >
                    Change Status
                  </Button>
                </Col>
              )}
            </Row>
          </Card>
          <Card key="basic-info" title={<Title level={5}>Basic information</Title>}>
            <Descriptions bordered column={1}>
              <Descriptions.Item key="cta-id" label="CTA ID">
                {data.ctaId}
              </Descriptions.Item>
              <Descriptions.Item key="first-time" label="First detect time">
                {dayjs(data.firstTime).format("YYYY-MM-DD HH:mm:ss")}
              </Descriptions.Item>
              <Descriptions.Item key="type" label="Type">
                {data.ctaType}
              </Descriptions.Item>
              <Descriptions.Item key="final-cta" label="Standardized CTA Content">
                {data.finalCta}
              </Descriptions.Item>
              <Descriptions.Item key="location" label="Location">
                {data.ctaType === "URL" ? (data.websiteArea || "Unknown Location") :
                  data.ctaType === "Phone" ? (data.phoneArea || "Unknown Area") :
                    data.countryCode || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item key="brands" label="Associated Brands">
                {data.relatedBrands}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          {data.ctaType === "URL" && (
            <>
              <Card key="url-info" title={<Title level={5}>Service Provider Information</Title>}>
                <Descriptions bordered column={1}>
                  <Descriptions.Item key="network" label="Network provider">
                    {data.networkProvider || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item key="registrar" label="Domain registrar">
                    {data.domainRegistrar || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item key="resolver" label="domainNameResolver">
                    {data.domainNameResolver || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {data.img === '1' ? (
                <Card
                  key="img-info"
                  title={<Title level={5}>CTA image</Title>}
                  bodyStyle={{ textAlign: 'center' }}
                >
                  <Image
                    src={imgUrl}
                    alt="CTA img"
                    style={{
                      width: '20%',
                      height: '20%',
                      display: 'inline-block'
                    }}
                  />
                </Card>
              ) : (
                <Card
                  key="img-info"
                  title={<Title level={5}>CTA image</Title>}
                  bodyStyle={{ textAlign: 'center' }}
                >
                  <Image
                    src={errorImg}
                    preview={false}
                  />
                </Card>
              )}
            </>
          )}
          {data.ctaType === "Phone" && (
            <Card key="phone-info" title={<Title level={5}>Service Provider Information</Title>}>
              <Descriptions bordered column={1}>
                <Descriptions.Item key="allocatee" label="Allocatee">
                  {data.allocatee}
                </Descriptions.Item>
                <Descriptions.Item
                  key="allocation-date"
                  label="Allocation Date"
                >
                  {data.allocationDate}
                </Descriptions.Item>
                <Descriptions.Item key="holder" label="Latest Holder">
                  {data.latestHolder}
                </Descriptions.Item>
                <Descriptions.Item
                  key="transfer-date"
                  label="Latest Transfer Date"
                >
                  {data.latestTransferDate}
                </Descriptions.Item>
                <Descriptions.Item key="area" label="Numbering Area">
                  {data.numberingArea}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {data.ctaType === "BankAccount" && (
            <Card key="url-info" title={<Title level={5}>Bank Information</Title>}>
              <Descriptions bordered column={1}>
                <Descriptions.Item key="resolver" label="FIName（Bank Name）">
                  {data.bankName || "-"}
                </Descriptions.Item>
                <Descriptions.Item key="resolver" label="BSBName（Branch Name）">
                  {data.bsbName || "-"}
                </Descriptions.Item>
                <Descriptions.Item key="resolver" label="Address（Branch Address）">
                  {data.address || "-"}
                </Descriptions.Item>
                <Descriptions.Item key="resolver" label="Suburb（City/Suburb）">
                  {data.suburb || "-"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card title={<Title level={5}>Related Conversations</Title>}>
          <Typography.Paragraph>
            This CTA has {" "}
            <span style={{ color: "#55B8FA" }}>{data.conversationCount} {" "}</span>
            related conversations, including {" "}
            <span style={{ color: "#FAD455" }}>
              {data.srNames?.length || 0} {" "}
            </span>
            case(s) opened.
          </Typography.Paragraph>
          <Table
            columns={conversationColumns}
            dataSource={conversationList}
            pagination={false}
            size="small"
            rowKey="externalRef"
          />
        </Card>
      </Col>

      <StatusModal
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          modalForm.resetFields();
        }}
        form={modalForm}
        statusModal={2}
        title="Change Status"
      />
    </Row>
  );
};

export default CTADetail;